import React, { useState, useEffect, useRef } from 'react';
import { track } from '../utils/analytics';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Collapse,
  Chip,
  IconButton,
  GlobalStyles
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import QueryFilters from './QueryFilters';
import { MOBILE_NAV_HEIGHT } from './nav/MobileBottomNav';
import QueryResults from './QueryResults';
import NLQueryInput from './NLQueryInput';
import NLInterpretation from './NLInterpretation';
import { useUrlParams, filtersToUrlParams } from '../utils/urlParamParser';
import axios from 'axios';
import { Link as RouterLink } from 'react-router-dom';
import config from '../config';
import { qbButtonSx, qbCardSx, qbColors, qbFonts, qbGhostButtonSx } from './queryBuilderTheme';
import { useFormat } from '../context/FormatContext';
import FormatSwitcher from './FormatSwitcher';
import { apiErrorText } from '../utils/apiError';

const getDefaultFilters = () => ({
  // Basic filters
  venue: null,
  start_date: null,
  end_date: null,
  leagues: [],
  teams: [],
  batting_teams: [],
  bowling_teams: [],
  players: [],
  batters: [],
  bowlers: [],

  // Match context
  innings: null,
  over_min: null,
  over_max: null,
  match_outcome: [],
  is_chase: null,
  chase_outcome: [],
  toss_decision: [],
  day_or_night: null,

  // Query mode (default is fully backward-compatible delivery mode)
  query_mode: 'delivery',

  // Batter filters
  bat_hand: null,

  // Bowler filters
  bowl_style: [],
  bowl_kind: [],

  // Delivery detail filters
  line: [],
  length: [],
  shot: [],
  shot_family: [],
  control: null,
  wagon_zone: [],
  dismissal: [],

  // Grouped result filters
  min_balls: null,
  max_balls: null,
  min_runs: null,
  max_runs: null,
  min_wickets: null,
  max_wickets: null,
  // Metric thresholds on grouped rows, 'metric:op:value' (e.g. 'average:gte:50')
  having: [],
  // Set by shared links (the connector's "Open in Hindsight"): match ids, excluded players,
  // match-context dimension filters ('bowler_over_number:gte:2') and the metric perspective.
  match_ids: [],
  exclude_batters: [],
  exclude_bowlers: [],
  dimension_filters: [],
  metrics_perspective: null,

  // Pagination
  limit: 1000,
  offset: 0,

  // International matches
  include_international: false,
  top_teams: 10,

  // Summary rows
  show_summary_rows: false
});

const parseSuggestionFragment = (suggestion) => {
  const text = String(suggestion || '').trim();
  if (!text) {
    return '';
  }

  const quotedMatch = text.match(/["']([^"']+)["']/);
  if (quotedMatch?.[1]) {
    return quotedMatch[1].trim();
  }

  const lowered = text
    .replace(/^try\s+(adding|using|including)\s+/i, '')
    .replace(/^add\s+/i, '')
    .replace(/^include\s+/i, '')
    .replace(/^group(?:ed)?\s+by\s+/i, 'grouped by ')
    .replace(/\s+to\s+see\b.*$/i, '')
    .replace(/\s+to\s+compare\b.*$/i, '')
    .replace(/\s+for\s+more\b.*$/i, '')
    .trim();

  return lowered.replace(/\.$/, '');
};

const buildRefinedQuery = (baseQuery, suggestion) => {
  const fragment = parseSuggestionFragment(suggestion);
  const source = String(baseQuery || '').trim();

  if (!fragment) {
    return source;
  }
  if (!source) {
    return fragment;
  }
  if (source.toLowerCase().includes(fragment.toLowerCase())) {
    return source;
  }
  return `${source} ${fragment}`.trim();
};

const ACTIVE_FILTER_KEYS = [
  'venue', 'start_date', 'end_date', 'leagues', 'teams', 'batting_teams', 'bowling_teams',
  'players', 'batters', 'bowlers', 'bat_hand', 'bowl_style', 'bowl_kind',
  'line', 'length', 'shot', 'shot_family', 'control', 'wagon_zone', 'dismissal',
  'innings', 'over_min', 'over_max',
  'match_outcome', 'is_chase', 'chase_outcome', 'toss_decision',
  'min_balls', 'max_balls', 'min_runs', 'max_runs', 'min_wickets', 'max_wickets', 'having',
  'match_ids', 'exclude_batters', 'exclude_bowlers', 'dimension_filters', 'metrics_perspective',
  'partnership_players'
];

const getActiveFilterCount = (filters, groupBy) => {
  const filterCount = ACTIVE_FILTER_KEYS.reduce((count, key) => {
    const value = filters[key];
    if (Array.isArray(value)) {
      return count + (value.length > 0 ? 1 : 0);
    }
    return count + (value !== null && value !== undefined && value !== '' ? 1 : 0);
  }, 0);

  return filterCount + (Array.isArray(groupBy) ? groupBy.length : 0);
};

const QueryBuilder = ({ isMobile }) => {
  const { formatParams, active, formats, selectFormat, loading: formatsLoading } = useFormat();
  // The auto-run from a shared link is scheduled; read the format at run time, not from the
  // render that scheduled it.
  const formatParamsRef = useRef(formatParams);
  formatParamsRef.current = formatParams;
  const { getFiltersFromUrl, getGroupByFromUrl, currentParams } = useUrlParams();

  
  const [filters, setFilters] = useState(getDefaultFilters);
  
  const [groupBy, setGroupBy] = useState([]);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [availableColumns, setAvailableColumns] = useState(null);
  const [, setQueryTab] = useState(0);
  const [, setIsAutoExecuting] = useState(false);
  const [hasLoadedFromUrl, setHasLoadedFromUrl] = useState(false);
  const [nlInterpretation, setNlInterpretation] = useState(null);
  const [nlConfidence, setNlConfidence] = useState('medium');
  const [nlSourceQuery, setNlSourceQuery] = useState('');
  const [nlRawFilters, setNlRawFilters] = useState({});
  const [nlRecommendedColumns, setNlRecommendedColumns] = useState([]);
  const [nlRecommendedChart, setNlRecommendedChart] = useState(null);
  // Conditions from the question the query could not apply (e.g. an unsupported metric threshold).
  const [nlWarnings, setNlWarnings] = useState([]);
  // /graphics sends ?graphic=1: open the graphic maker once the grouped result is in.
  const [autoGraphic] = useState(() => new URLSearchParams(window.location.search).get('graphic') === '1');
  const [isApplyingSuggestion, setIsApplyingSuggestion] = useState(false);
  const [nlExpanded, setNlExpanded] = useState(true);
  // Phones open with the filter card folded: the NL search is the quick path there, and the
  // full field list is ~2.5 screens. (A shared link or NL run still expands/collapses it below.)
  const [filtersCollapsed, setFiltersCollapsed] = useState(
    () => typeof window !== 'undefined' && window.matchMedia?.('(max-width: 899.95px)').matches,
  );
  // ball_aggregation toggle: only meaningful when ball or ball_in_spell is in
  // the active group_by; otherwise the backend ignores it.
  const [ballAggregation, setBallAggregation] = useState('snapshot');
  const executeQueryRef = useRef(null);
  // The exact query string of the last run: the Image & embed button snapshots this query.
  const [lastQueryString, setLastQueryString] = useState('');
  const nlInputRef = useRef(null);
  // The parsed question's log row: sent with its runs (nl_ref) so the API records how they went.
  // Kept out of the page URL, so a shared link's runs aren't counted against the question.
  const nlLogRefRef = useRef(null);

  // Load filters from URL on mount
  useEffect(() => {
    const params = new URLSearchParams(currentParams || '');
    const nlQuery = params.get('nl');

    if (nlQuery && !hasLoadedFromUrl) {
      setHasLoadedFromUrl(true);
      setNlExpanded(true);
      setFiltersCollapsed(false);
      setTimeout(() => {
        nlInputRef.current?.runQuery?.(nlQuery);
      }, 100);
      return;
    }

    const urlFilters = getFiltersFromUrl();
    const urlGroupBy = getGroupByFromUrl();
    
    // Wait for the format list: until it loads, ?fmt=mens-odi is not yet resolvable and the
    // page sits on its fallback ("All formats"), so a shared ODI link ran as ALL and mixed in
    // T20 numbers.
    if (formatsLoading) return;

    // Only a link with something to run auto-runs. The site stamps ?fmt= on every URL, and a
    // format alone used to fire an unfiltered, ungrouped query over the whole table.
    const runnable = new URLSearchParams(currentParams || '');
    runnable.delete('fmt');
    if (currentParams && [...runnable.keys()].length > 0 && !hasLoadedFromUrl) {
      setFilters(prevFilters => ({
        ...prevFilters,
        ...urlFilters
      }));
      setGroupBy(urlGroupBy);
      setHasLoadedFromUrl(true);
      setIsAutoExecuting(true);
      setNlExpanded(false);
      setFiltersCollapsed(true);
      
      setTimeout(() => {
        executeQueryFromUrl(urlFilters, urlGroupBy);
      }, 500);
    }
  }, [currentParams, hasLoadedFromUrl, formatsLoading]);
  
  // Fetch available columns
  useEffect(() => {
    const fetchAvailableColumns = async () => {
      try {
        const response = await axios.get(
          `${config.API_URL}/query/deliveries/columns`,
          { params: formatParams },
        );
        setAvailableColumns(response.data);
      } catch (error) {
        console.error('Error fetching available columns:', error);
        setError('Failed to load column metadata');
      }
    };
    
    fetchAvailableColumns();
    // Re-fetch on format change: the dropdown values are cached per format on the backend, so
    // an ODI query must not be offered the T20 competition list.
  }, [formatParams.format, formatParams.gender]);
  
  // Query params for a run, scoped to the selected format. ODIs are all between national sides,
  // so the T20 league/international switch and top-teams cut do not apply to them; a T20-only
  // switch left on from an earlier T20 query must not narrow an ODI one.
  const buildQueryParams = (source) => {
    const fp = formatParamsRef.current;
    const params = new URLSearchParams(fp);
    const internationalOnly = fp.format === 'ODI' || fp.format === 'TEST';
    Object.entries(source).forEach(([key, value]) => {
      if (key === 'query_mode' && value === 'delivery') return;
      if (internationalOnly && (key === 'include_international' || key === 'top_teams')) return;
      if (key === 'top_teams' && !source.include_international) return;
      if (value !== null && value !== undefined && value !== '') {
        if (Array.isArray(value)) {
          value.forEach((item) => params.append(key, item));
        } else {
          params.append(key, value);
        }
      }
    });
    return params;
  };

  const executeQueryFromUrl = async (urlFilters, urlGroupBy) => {
    try {
      setLoading(true);
      setError(null);
      
      const params = buildQueryParams(urlFilters);

      urlGroupBy.forEach(col => params.append('group_by', col));
      if (ballAggregation && ballAggregation !== 'snapshot') {
        params.append('ball_aggregation', ballAggregation);
      }

      const response = await axios.get(`${config.API_URL}/query/deliveries?${params.toString()}`);
      setResults(response.data);
      setLastQueryString(params.toString());
      setQueryTab(1);
      setNlExpanded(false);
      setFiltersCollapsed(true);

    } catch (error) {
      console.error('Error executing query from URL:', error);
      setError(apiErrorText(error, 'Failed to execute query'));
    } finally {
      setLoading(false);
      setIsAutoExecuting(false);
    }
  };
  
  const executeQuery = async () => {
    track('query_run', { group_by: groupBy.join(',') || null });
    try {
      setLoading(true);
      setError(null);
      
      const params = buildQueryParams(filters);

      groupBy.forEach(col => params.append('group_by', col));
      if (ballAggregation && ballAggregation !== 'snapshot') {
        params.append('ball_aggregation', ballAggregation);
      }

      const apiParams = new URLSearchParams(params);
      if (nlLogRefRef.current) apiParams.append('nl_ref', nlLogRefRef.current);
      const response = await axios.get(`${config.API_URL}/query/deliveries?${apiParams.toString()}`);
      setResults(response.data);
      setLastQueryString(params.toString());
      setQueryTab(1);
      setNlExpanded(false);
      setFiltersCollapsed(true);

      // Update after the request succeeds so parsing/execution does not visibly
      // churn the URL while the UI is still resolving state.
      const newParams = filtersToUrlParams(filters, groupBy);
      const newUrl = `${window.location.pathname}?${newParams}`;
      window.history.replaceState({}, '', newUrl);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
    } catch (error) {
      console.error('Error executing query:', error);
      setError(apiErrorText(error, 'Failed to execute query'));
    } finally {
      setLoading(false);
    }
  };

  // Keep ref current for async callbacks
  executeQueryRef.current = executeQuery;

  const clearFilters = () => {
    setFilters(getDefaultFilters());
    setGroupBy([]);
    setResults(null);
    setQueryTab(0);
    setHasLoadedFromUrl(true);
    setIsAutoExecuting(false);
    setNlInterpretation(null);
    setNlConfidence('medium');
    setNlSourceQuery('');
    nlLogRefRef.current = null;
    setNlRawFilters({});
    setNlRecommendedColumns([]);
    setNlRecommendedChart(null);
    setNlExpanded(true);
    setFiltersCollapsed(false);
    window.history.replaceState({}, '', window.location.pathname);
  };
  
  const handleNLFilters = ({
    queryText,
    filters: nlFilters,
    groupBy: nlGroupBy,
    explanation,
    confidence,
    suggestions,
    recommendedColumns,
    recommendedChart,
    interpretation,
    warnings,
    logRef,
    format: questionFormat,
  }) => {
    nlLogRefRef.current = logRef || null;
    // Reset to defaults then apply NL filters
    const defaultFilters = getDefaultFilters();

    const newFilters = { ...defaultFilters, ...(nlFilters || {}) };
    const newGroupBy = nlGroupBy || [];
    const interpretationPayload = interpretation || {
      summary: explanation || '',
      parsed_entities: [],
      suggestions: suggestions || [],
    };

    setFilters(newFilters);
    setGroupBy(newGroupBy);
    setNlInterpretation(interpretationPayload);
    setNlConfidence(confidence || 'medium');
    setNlSourceQuery((queryText || '').trim());
    setNlRawFilters(nlFilters || {});
    setNlRecommendedColumns(Array.isArray(recommendedColumns) ? recommendedColumns : []);
    setNlRecommendedChart(recommendedChart || null);
    setNlWarnings(Array.isArray(warnings) ? warnings : []);
    setQueryTab(0);
    setHasLoadedFromUrl(true);
    setNlExpanded(false);
    setFiltersCollapsed(true);

    // Keep the typed NL query shareable until execution rewrites the URL with
    // structured filters.
    const newUrl = `${window.location.pathname}?nl=${encodeURIComponent((queryText || '').trim())}`;
    window.history.replaceState({}, '', newUrl);

    // A question that names a format ("in ODIs", "IPL") runs in it: switch the site like the format
    // picker does (and like opening an ODI preview), and point this run at it now rather than after
    // the re-render.
    if (questionFormat && active && questionFormat !== active.format) {
      const gender = active.gender || 'male';
      const target = (formats || []).find((f) => f.format === questionFormat && f.gender === gender && f.available);
      if (target) {
        selectFormat(target.slug);
        formatParamsRef.current = { format: target.format, gender: target.gender };
      }
    }

    // Auto-execute for high confidence, just populate for medium/low
    if (confidence === 'high') {
      setIsAutoExecuting(true);
      setTimeout(() => {
        executeQueryRef.current();
      }, 300);
    }
  };

  const dismissInterpretation = () => {
    setNlInterpretation(null);
    setNlRawFilters({});
  };

  const applySuggestion = async (suggestion) => {
    if (!nlInputRef.current?.runQuery) {
      return;
    }

    const refinedQuery = buildRefinedQuery(nlSourceQuery, suggestion);
    if (!refinedQuery) {
      return;
    }

    try {
      setIsApplyingSuggestion(true);
      setError(null);
      await nlInputRef.current.runQuery(refinedQuery);
    } finally {
      setIsApplyingSuggestion(false);
    }
  };

  const hasValidFilters = () => {
    const filterKeys = [
      'venue', 'start_date', 'end_date', 'leagues', 'teams', 'batting_teams', 'bowling_teams',
      'players', 'batters', 'bowlers', 'bat_hand', 'bowl_style', 'bowl_kind',
      'line', 'length', 'shot', 'shot_family', 'control', 'wagon_zone', 'partnership_players',
      'innings', 'over_min', 'over_max',
      'match_outcome', 'is_chase', 'chase_outcome', 'toss_decision',
      'min_balls', 'max_balls', 'min_runs', 'max_runs', 'min_wickets', 'max_wickets', 'having'
    ];
    
    return filterKeys.some(key => {
      const value = filters[key];
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      return value !== null && value !== undefined && value !== '';
    });
  };

  const activeCount = getActiveFilterCount(filters, groupBy);
  const canExecute = !!availableColumns && (hasValidFilters() || groupBy.length > 0) && !loading && !isApplyingSuggestion;
  const hasResults = !!results;
  const nlActive = !!nlSourceQuery;
  
  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100vw',
        boxSizing: 'border-box',
        // clip, not hidden, here and on html/body below: hidden turns overflow-y into auto,
        // making each a (non-scrolling) scroll container that position: sticky attaches to.
        overflowX: 'clip',
        mx: { xs: 0, md: -3 },
        mt: -3,
        mb: -2,
        minHeight: '100vh',
        bgcolor: qbColors.bg,
        color: qbColors.textHi,
        fontFamily: qbFonts.body,
        px: { xs: '14px', md: '30px' },
        py: { xs: '18px', md: '30px' },
        '& .MuiTypography-root': { fontFamily: qbFonts.body },
        '& .MuiPaper-root': { backgroundImage: 'none' },
        '& .MuiInputBase-root': {
          bgcolor: `${qbColors.input} !important`,
          color: qbColors.textHi,
          borderRadius: '12px',
        },
        '& .MuiInputLabel-root, & .MuiFormLabel-root': {
          color: qbColors.textLo,
          fontFamily: qbFonts.mono,
          fontSize: 11,
          letterSpacing: '0.06em',
        },
        '& .MuiOutlinedInput-notchedOutline': { borderColor: qbColors.borderStrong },
        '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: 'rgba(182,242,74,0.42)',
        },
        '& .MuiChip-root': { fontFamily: qbFonts.mono },
        '& .MuiTableCell-root': {
          borderColor: qbColors.border,
          color: qbColors.textMed,
          fontFamily: qbFonts.body,
        },
        '& .MuiTableHead-root .MuiTableCell-root': {
          bgcolor: `${qbColors.surface2} !important`,
          color: qbColors.textLo,
          fontFamily: qbFonts.mono,
        },
      }}
    >
      <GlobalStyles
        styles={{
          'html, body, #root': {
            backgroundColor: qbColors.bg,
            overflowX: 'clip',
          },
          '.MuiAutocomplete-popper .MuiPaper-root, .MuiPopover-paper, .MuiMenu-paper': {
            backgroundColor: qbColors.surface3,
            color: qbColors.textMed,
            border: `1px solid ${qbColors.borderStrong}`,
            boxShadow: '0 24px 48px -18px rgba(0,0,0,0.75)',
          },
          '.MuiAutocomplete-popper .MuiAutocomplete-option': {
            color: qbColors.textMed,
            fontFamily: qbFonts.body,
          },
          '.MuiAutocomplete-popper .MuiAutocomplete-option[aria-selected="true"], .MuiAutocomplete-popper .MuiAutocomplete-option.Mui-focused': {
            backgroundColor: 'rgba(182,242,74,0.12) !important',
            color: qbColors.textHi,
          },
          '.MuiAutocomplete-noOptions, .MuiAutocomplete-loading': {
            color: qbColors.textLo,
          },
        }}
      />
      <Box sx={{ width: '100%', maxWidth: 1180, mx: 'auto', minWidth: 0 }}>
        {/* The app header (nav tabs / mobile top bar) now brands every page, so the query
            builder's own "Hindsight / Query Builder" + Explore row was a duplicate. */}
        <Box sx={{ height: { xs: 16, md: 28 } }} />

        {!hasResults && (
          <Box sx={{ mb: { xs: 2.8, md: 4 } }}>
            <Typography
              sx={{
                fontFamily: qbFonts.mono,
                fontSize: 10,
                letterSpacing: '0.16em',
                color: qbColors.accent,
                textTransform: 'uppercase',
                mb: 1,
              }}
            >
              00 / Query Builder
            </Typography>
            <Typography
              component="h1"
              sx={{
                fontFamily: qbFonts.display,
                fontSize: { xs: 30, md: 42 },
                lineHeight: 1.02,
                fontWeight: 700,
                color: qbColors.textHi,
                mb: 1,
              }}
            >
              Ask the ball-by-ball data anything.
            </Typography>
            <Typography sx={{ color: qbColors.textLo, fontSize: { xs: 14, md: 15 } }}>
              Search in plain English, or build a query with filters and grouping.
            </Typography>
          </Box>
        )}

        {/* minmax(0, 1fr): an implicit grid column sizes to its widest child's content, which
            stretched every card to ~520px on a 390px phone and clipped the right edge. */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: { xs: '18px', md: '28px' }, minWidth: 0 }}>
          {(!hasResults || nlExpanded) ? (
            <NLQueryInput
              ref={nlInputRef}
              onFiltersGenerated={handleNLFilters}
              disabled={loading || isApplyingSuggestion}
            />
          ) : (
            <Paper elevation={0} sx={{ ...qbCardSx, p: 1.25, borderColor: 'rgba(182,242,74,0.18)' }}>
              <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 1.2, flexWrap: 'wrap' }}>
                <Box sx={{ width: 32, height: 32, borderRadius: '10px', display: 'grid', placeItems: 'center', bgcolor: qbColors.accentSoft }}>
                  <SearchIcon sx={{ color: qbColors.accent, fontSize: 18 }} />
                </Box>
                <Typography sx={{ color: nlActive ? qbColors.textHi : qbColors.textLo, flex: 1, minWidth: { xs: 0, sm: 180 }, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {nlActive ? `"${nlSourceQuery}"` : 'Built from filters - no natural-language query'}
                </Typography>
                <Button
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => setNlExpanded(true)}
                  sx={qbGhostButtonSx}
                >
                  New search
                </Button>
              </Box>
            </Paper>
          )}

          {nlInterpretation && (
            <NLInterpretation
              interpretation={nlInterpretation}
              confidence={nlConfidence}
              rawFilters={nlRawFilters}
              onSuggestionClick={applySuggestion}
              onClose={dismissInterpretation}
              disabled={loading || isApplyingSuggestion}
            />
          )}

          {nlWarnings.length > 0 && (
            <Alert severity="warning" onClose={() => setNlWarnings([])} sx={{ bgcolor: 'rgba(240,180,41,0.08)', color: qbColors.textHi, border: '1px solid rgba(240,180,41,0.28)' }}>
              {nlWarnings.join(' ')}
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ bgcolor: 'rgba(229,72,77,0.1)', color: qbColors.textHi, border: `1px solid rgba(229,72,77,0.28)` }}>
              {error}
            </Alert>
          )}

          {/* overflow: clip, not hidden: hidden makes the card a scroll container, which stops the
              Execute bar below from sticking on phones. */}
          <Paper elevation={0} sx={{ ...qbCardSx, overflow: 'clip' }}>
            <Box
              onClick={() => setFiltersCollapsed((prev) => !prev)}
              sx={{
                p: { xs: 1.6, md: 2.4 },
                display: 'flex',
                alignItems: 'center',
                // Wraps on phones: the format switcher and chip drop under the title instead of
                // squeezing "Filters & Grouping" into three lines.
                flexWrap: 'wrap',
                gap: 1.25,
                cursor: 'pointer',
              }}
            >
              <Box sx={{ flex: '1 1 180px', minWidth: 0 }}>
                <Typography sx={{ fontFamily: qbFonts.mono, color: qbColors.accent, fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase' }}>
                  01 / Filters
                </Typography>
                <Typography sx={{ fontFamily: qbFonts.display, fontSize: { xs: 18, md: 23 }, fontWeight: 700, whiteSpace: 'nowrap' }}>
                  Filters & Grouping
                </Typography>
              </Box>
              {/* Format lives here, not in the site nav: it is a query filter like any other,
                  and its only real consumer is this page. Elsewhere the format is implied by
                  what you are looking at -- a scorecard is one match, a preview one fixture. */}
              <FormatSwitcher />
              <Chip
                label={`${activeCount} active`}
                sx={{
                  bgcolor: qbColors.accent,
                  color: qbColors.bg,
                  height: 26,
                  fontSize: 11,
                  fontWeight: 700,
                }}
              />
              <IconButton size="small" sx={{ color: qbColors.textLo }}>
                {filtersCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
              </IconButton>
            </Box>

            <Collapse in={!filtersCollapsed}>
              <Box sx={{ px: { xs: 1.6, md: 2.4 }, pb: 2.4, minWidth: 0 }}>
                <QueryFilters
                  filters={filters}
                  setFilters={setFilters}
                  groupBy={groupBy}
                  setGroupBy={setGroupBy}
                  availableColumns={availableColumns}
                  isMobile={isMobile}
                />
              </Box>
            </Collapse>

            <Box
              sx={{
                p: { xs: 2, md: 2.4 },
                borderTop: `1px solid ${qbColors.border}`,
                // Phones: with the filters open, Execute stays reachable above the bottom nav
                // instead of ~2.5 screens down.
                ...(isMobile && !filtersCollapsed && {
                  position: 'sticky',
                  bottom: `calc(${MOBILE_NAV_HEIGHT}px + env(safe-area-inset-bottom, 0px))`,
                  zIndex: 2,
                  bgcolor: qbColors.surface1,
                  boxShadow: '0 -8px 16px rgba(0,0,0,0.35)',
                }),
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                gap: 1.5,
                alignItems: isMobile ? 'stretch' : 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box sx={{ display: 'flex', gap: 1, flexDirection: isMobile ? 'column' : 'row' }}>
                <Button
                  variant="contained"
                  onClick={executeQuery}
                  disabled={!canExecute}
                  startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
                  sx={{ ...qbButtonSx, minHeight: 46, px: 2.2 }}
                >
                  {loading ? 'Querying...' : 'Execute query'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={clearFilters}
                  disabled={loading}
                  sx={{ ...qbGhostButtonSx, minHeight: 46, px: 2 }}
                >
                  Clear all
                </Button>
              </Box>
              <Typography sx={{ color: qbColors.textFaint, fontFamily: qbFonts.mono, fontSize: 11, textAlign: isMobile ? 'center' : 'right' }}>
                {!canExecute && !loading && 'Add a filter or group-by dimension to run'}
                {canExecute && !loading && groupBy.length > 0 && `Ready - grouping by ${groupBy.join(', ')}`}
                {canExecute && !loading && groupBy.length === 0 && 'Ready - returns individual deliveries'}
              </Typography>
            </Box>
          </Paper>

          {hasResults ? (
            <Box>
              <Typography
                sx={{
                  fontFamily: qbFonts.mono,
                  color: qbColors.accent,
                  fontSize: 10,
                  letterSpacing: '0.16em',
                  textTransform: 'uppercase',
                  mb: 1,
                }}
              >
                02 / Results
              </Typography>
              <QueryResults
                results={results}
                groupBy={groupBy}
                filters={filters}
                recommendedColumns={nlRecommendedColumns}
                recommendedChart={nlRecommendedChart || results?.metadata?.recommended_chart || null}
                nlInterpretation={null}
                nlConfidence={nlConfidence}
                nlRawFilters={nlRawFilters}
                nlSourceQuery={nlSourceQuery}
                onSuggestionClick={applySuggestion}
                onDismissInterpretation={dismissInterpretation}
                interpretationDisabled={loading || isApplyingSuggestion}
                isMobile={isMobile}
                ballAggregation={ballAggregation}
                apiQueryString={lastQueryString}
                autoOpenGraphic={autoGraphic}
                onBallAggregationChange={(mode) => {
                  setBallAggregation(mode);
                  setTimeout(() => executeQueryRef.current && executeQueryRef.current(), 0);
                }}
              />
            </Box>
          ) : (
            <Paper
              elevation={0}
              sx={{
                ...qbCardSx,
                p: { xs: 3, md: 4 },
                borderStyle: 'dashed',
                display: 'grid',
                placeItems: 'center',
                textAlign: 'center',
                color: qbColors.textLo,
              }}
            >
              <SearchIcon sx={{ color: qbColors.accent, mb: 1 }} />
              <Typography sx={{ fontFamily: qbFonts.display, color: qbColors.textHi, fontSize: 20, fontWeight: 700 }}>
                No results yet
              </Typography>
              <Typography sx={{ maxWidth: 420, mt: 0.5 }}>
                Search in plain English or set a filter and hit Execute.
              </Typography>
            </Paper>
          )}
        </Box>

        <Box sx={{ mt: { xs: 3, md: 5 }, pt: 2, borderTop: `1px solid ${qbColors.border}`, display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'space-between' }}>
          <Typography sx={{ color: qbColors.textFaint, fontFamily: qbFonts.mono, fontSize: 11 }}>
            {/* Cricsheet is one of several sources (the delivery detail comes from elsewhere), so
                point at the full list rather than naming one. */}
            Hindsight © {new Date().getFullYear()} ·{' '}
            <Box component={RouterLink} to="/credits" sx={{ color: 'inherit', textDecorationColor: 'rgba(255,255,255,0.3)' }}>
              data sources
            </Box>
          </Typography>
          <Typography sx={{ color: qbColors.textFaint, fontFamily: qbFonts.mono, fontSize: 11 }}>
            Shareable query URL updates on execute
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default QueryBuilder;
